import { Marker } from '@maptiler/sdk';

export class MarkerManager {
  private static eventListeners = new Map<Marker, Map<string, EventListener>>();
  private static markerBindings = new Map<Marker, Marker>();

  static addEventListener(marker: Marker, eventType: string, listener: EventListener): void {
    marker.getElement().addEventListener(eventType, listener);
    
    if (!this.eventListeners.has(marker)) {
      this.eventListeners.set(marker, new Map());
    }
    this.eventListeners.get(marker)!.set(eventType, listener);
  }
  
  static removeEventListener(marker: Marker, eventType: string): void {
    const markerListeners = this.eventListeners.get(marker);
    if (markerListeners && markerListeners.has(eventType)) {
      const listener = markerListeners.get(eventType)!;
      marker.getElement().removeEventListener(eventType, listener);
      markerListeners.delete(eventType);
      
      if (markerListeners.size === 0) {
        this.eventListeners.delete(marker);
      }
    }
  }
  
  static clearAllEventListeners(marker: Marker): void {
    const markerListeners = this.eventListeners.get(marker);
    if (markerListeners) {
      const element = marker.getElement();
      markerListeners.forEach((listener, eventType) => {
        element.removeEventListener(eventType, listener);
      });
      this.eventListeners.delete(marker);
    }
  }
  
  static hasEventListener(marker: Marker, eventType?: string): boolean {
    const markerListeners = this.eventListeners.get(marker);
    if (!markerListeners) return false;
    
    if (eventType) {
      return markerListeners.has(eventType);
    }
    return markerListeners.size > 0;
  }
  
  static getEventListenerCount(marker: Marker): number {
    const markerListeners = this.eventListeners.get(marker);
    return markerListeners ? markerListeners.size : 0;
  }

  static clearAll(): void {
    this.eventListeners.forEach((listeners, marker) => {
      const element = marker.getElement();
      listeners.forEach((listener, eventType) => {
        element.removeEventListener(eventType, listener);
      });
    });
    this.eventListeners.clear();
  }

  static getMarkerCount(): number {
    return this.eventListeners.size;
  }

  static setVisible(markerElement: any, isVisible: boolean) {
    markerElement.getElement().style.display = isVisible ? 'block' : 'none';
  }

  static getVisible(markerElement: any): boolean {
    const element = markerElement.getElement();
    return element.style.display !== 'none';
  }


  static setIcon(markerElement: any, iconUrl: string, width: number = 32, height: number = 32) {
    const el = document.createElement('div');
    el.className = 'custom-marker';
    el.style.backgroundImage = `url(${iconUrl})`;
    el.style.width = `${width}px`;
    el.style.height = `${height}px`;
    el.style.backgroundSize = 'cover';
    el.style.backgroundRepeat = 'no-repeat';
    el.style.backgroundPosition = 'center';
    el.style.cursor = 'pointer';
    el.style.pointerEvents = 'auto'; // Ensure clicks are enabled
    el.style.position = 'relative'; // Proper positioning
    el.style.zIndex = '1'; // Ensure it's above other elements
    
    // Replace the marker's element content
    const element = markerElement.getElement();
    element.innerHTML = '';
    element.appendChild(el);
    element.style.pointerEvents = 'auto'; // Enable on parent too

    MarkerManager.setAttr(markerElement, 'iconUrl', iconUrl);
  }

  static getIcon(markerElement: any): string {
    return MarkerManager.getAttr(markerElement, 'iconUrl') || '';
  }

  static setTitle(markerElement: any, title: string) {
    markerElement.getElement().title = title;
    MarkerManager.setAttr(markerElement, 'name', title);
  }

  static getTitle(markerElement: any): string {
    return markerElement.getElement().title;
  }

  static setZIndex(markerElement: any, zIndex: number) {
    markerElement.getElement().style.zIndex = zIndex.toString();
  }

  static setAttr(markerElement: any, attr: string, value: any) {
    (markerElement as any)[attr] = value;
  }

  static getAttr(markerElement: any, attr: string): any {
    return (markerElement as any)[attr];
  }

  static adjustInvalidCoordinates(lat: number, lng: number): {latitude: number, longitude: number} {
    let adjustedLat = lat;
    let adjustedLng = lng;
    if (lat < -90 || lat > 90) {
      adjustedLat = 0;
    }
    if (lng < -180 || lng > 180) {
      adjustedLng = 0;
    }
    
    return {latitude: adjustedLat, longitude: adjustedLng};
  }

  static setClickable(markerElement: any, isClickable: boolean) {
    markerElement.getElement().style.pointerEvents = isClickable ? 'auto' : 'none';
  }

  static bindTo(followerMarker: Marker, leaderMarker: Marker): void {
    // Sync initial position
    const initialPosition = leaderMarker.getLngLat();
    followerMarker.setLngLat([initialPosition.lng, initialPosition.lat]);

    // Create sync function
    const syncPosition = () => {
      const newPosition = leaderMarker.getLngLat();
      followerMarker.setLngLat([newPosition.lng, newPosition.lat]);
    };

    // Listen to position changes
    leaderMarker.on('drag', syncPosition);
    leaderMarker.on('dragend', syncPosition);

    // Store the binding for cleanup
    MarkerManager.markerBindings.set(followerMarker, leaderMarker);
    MarkerManager.setAttr(followerMarker, 'syncFunction', syncPosition);
  }

  static unBindFrom(followerMarker: Marker): void {
    const leaderMarker = MarkerManager.markerBindings.get(followerMarker);
    const syncFunction = MarkerManager.getAttr(followerMarker, 'syncFunction');
    
    if (leaderMarker && syncFunction) {
      leaderMarker.off('drag', syncFunction);
      leaderMarker.off('dragend', syncFunction);
      MarkerManager.markerBindings.delete(followerMarker);
    }
  }
}
